import { Box, Typography } from "@mui/material";
import moment from "moment";
import tokens from "src/shared/theme/tokens";

interface CreditReportingAcknowledgementProps {
  residentName: string;
  unitNumber: string;
  property: any;
}

const CreditReportingAcknowledgement = ({
  residentName,
  unitNumber,
  property,
}: CreditReportingAcknowledgementProps) => {
  const address = `${property?.name}, ${property?.streetAddress}, ${property?.city},${property?.state}-${property?.zip}`;
  return (
    <div
      style={{
        maxHeight: "500px", // Set the maximum height to 400px
        overflowY: "auto", // Enable vertical scrolling if content exceeds 400px
        padding: "20px",
        border: `1px solid ${tokens.colors.border}`, // Optional: Add a border for better visibility
        borderRadius: "8px", // Optional: Rounded corners for styling
      }}
    >
      <Typography variant="h6" gutterBottom>
        Credit Reporting Program Agreement
      </Typography>

      <Typography variant="body1" gutterBottom>
        This Credit Reporting Program agreement is entered between -
        <strong>Leaseholder:</strong> {residentName} <br /> and{" "}
        <strong>the Owner :</strong> {property?.ownerName} of {property?.name}
        <br />
        <strong>Date:</strong> {moment().format("MM-DD-YYYY")}
      </Typography>

      <Typography variant="body1" gutterBottom>
        The Leaseholder is opting into a monthly credit reporting program (the
        "Program") in connection with the Lease Agreement for the premises
        located at{" "}
        <strong>
          {unitNumber} {address}
        </strong>
        . If any conflict arises between this agreement and the Lease Agreement,
        the terms of this Agreement shall control.
      </Typography>

      {/* Section I */}
      <Typography variant="h5" gutterBottom>
        I. Participation and Fees
      </Typography>
      <Typography variant="body1" gutterBottom>
        By enrolling, the Leaseholder agrees to pay a monthly Credit Reporting
        Program Fee (defined below) for the duration of the lease. This fee
        shall be paid in the same manner and at the same time as rent, as
        outlined in the Lease Agreement.
      </Typography>
      <Typography variant="body1" gutterBottom>
        Participation in the Program does not modify or waive any other terms of
        the Lease Agreement, which remains in full force and effect unless
        otherwise agreed in writing.
      </Typography>
      <Typography variant="body1" gutterBottom>
        In accordance with applicable state and local laws, the Leaseholder
        agrees to pay a monthly fee of no less than $6 ("Credit Reporting
        Program Fee") for the reporting of rental payment history to the
        consumer reporting agency. This fee will appear on the Leaseholder's
        billing statement and is required each month for the duration of the
        lease unless the Leaseholder opts out (see Section IV).
      </Typography>

      {/* Section II */}
      <Typography variant="h5" gutterBottom>
        II. Credit Reporting
      </Typography>
      <Typography variant="body1" gutterBottom>
        The Owner or its credit reporting partner may report:
        <ul>
          <li>Timely rent payments</li>
          <li>Delinquencies</li>
          <li>Resolutions of any past due payments</li>
        </ul>
      </Typography>
      <Typography variant="body1" gutterBottom>
        Leaseholder understands and acknowledges that such credit reporting may
        positively or negatively affect the Leaseholder's credit profile or
        score, depending on payment activity.
      </Typography>
      <Typography variant="body1" gutterBottom>
        LEASEHOLDER ACKNOWLEDGES AND AGREES that, except as required by
        applicable law or specifically stated herein, the Owner shall bear no
        liability for any impact this reporting may have on the Leaseholder's
        credit history, score, or report.
      </Typography>

      {/* Section III */}
      <Typography variant="h5" gutterBottom>
        III. Termination
      </Typography>
      <Typography variant="body1" gutterBottom>
        The Owner may terminate the Program at any time upon written notice to
        Leaseholder. Upon termination, the Owner and its credit reporting
        partner will cease reporting payment history to consumer reporting
        agencies.
      </Typography>

      {/* Section IV */}
      <Typography variant="h5" gutterBottom>
        IV. Opting Out
      </Typography>
      <Typography variant="body1" gutterBottom>
        The Leaseholder may opt out of the Program at any time by either:
        <ul>
          <li>
            Visiting the Settings section in the TenantPort resident portal,
            navigating to Rent Reporting, and clicking "Opt Out"
          </li>
          <li>Emailing support@credbuild.com with a request to opt out</li>
        </ul>
      </Typography>

      <Typography variant="body1" gutterBottom>
        The Leaseholder understands:
        <ul>
          <li>
            The Credit Reporting Program Fee will continue until they opt out.
          </li>
          <li>
            The opt-out becomes effective the following full calendar month.
          </li>
          <li>The fee for the month of opting out will not be refunded.</li>
        </ul>
      </Typography>

      {/* Section V */}
      <Typography variant="h5" gutterBottom>
        V. Leaseholder Acknowledgements
      </Typography>
      <Typography variant="body1" gutterBottom>
        <ul style={{ marginLeft: "20px", listStyleType: "disc" }}>
          <li>
            The Leaseholder agrees to remit all Credit Reporting Program Fee
            payments on time.
          </li>
          <li>
            The Leaseholder understands that failure to pay this fee may be
            treated as a material default of the Lease Agreement, potentially
            resulting in late fees or eviction.
          </li>
          <li>
            The Leaseholder understands that their payment history may be shared
            with third parties (e.g., credit reference agencies, financial
            institutions, employers, past landlords).
          </li>
          <li>
            The Leaseholder acknowledges that third-party contractors may
            facilitate reporting services on behalf of the Owner.
          </li>
          <li>
            The Leaseholder confirms they have had ample opportunity to ask
            questions about this Program.
          </li>
        </ul>
      </Typography>
    </div>
  );
};

export default CreditReportingAcknowledgement;
